import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'

export type UnlocodeSearchItem = {
  code: string
  name: string
}

const MIN_QUERY_LENGTH = 2

export type UseUnlocodeSearchParams = {
  country: string | null
  /** Raw search string (debounced inside the hook). */
  query: string
  limit?: number
  enabled?: boolean
}

/**
 * Server-side UN/LOCODE search via `GET /v1/config/unlocodes`.
 * Runs only when `country` is a 2-letter code and debounced `query` has at least 2 non-space characters.
 */
export function useUnlocodeSearch({
  country,
  query,
  limit: maxResults = 50,
  enabled: enabledOption = true,
}: UseUnlocodeSearchParams) {
  const countryUpper = (country ?? '').trim().toUpperCase()
  const debouncedQuery = useDebouncedValue(query.trim(), 280)
  const hasValidCountry = countryUpper.length === 2
  const hasValidQuery = debouncedQuery.length >= MIN_QUERY_LENGTH
  const enabled = enabledOption && hasValidCountry && hasValidQuery

  return useQuery({
    queryKey: ['unlocodes', 'search', countryUpper, debouncedQuery, maxResults],
    queryFn: async () => {
      const searchParamName = 'q'
      const response = await apiClient.get<{ items: UnlocodeSearchItem[] }>(
        '/v1/config/unlocodes',
        {
          params: {
            country: countryUpper,
            [searchParamName]: debouncedQuery,
            limit: maxResults,
          },
        },
      )
      return response.data.items
    },
    enabled,
    placeholderData: (previousData) => previousData,
    staleTime: 60_000,
  })
}

export { MIN_QUERY_LENGTH as UNLOCODE_SEARCH_MIN_QUERY_LENGTH }
